import Card from '../../components/card/Card';
import Spinner from '../../components/Spinner/spinner';
import P from '../../components/texts/p/P';
import Button from '../../components/button/Button';
import Icon from '../../components/icon/Icon';
import DropDown from '../../components/dropdown/DropDown';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import {
  deleteContractFile,
  getTemplates,
  sendContractTemplate,
  signContract,
  signManualContract,
} from '../../services/InstrumentationService';
import {
  clientData,
  contractData,
  manualContractData,
} from '../../types/interfaces';
import {
  APPLICATION_STATUS,
  CONTRACTS_TYPES,
  CURRENCY_CODES,
} from '../../utils/Constants';
import SignatureModal from './InstrumentationModals/SignatureModal';
import ManualSignatureModal from './InstrumentationModals/ManualSignatureModal';
import ConfirmSignsModal from './InstrumentationModals/ConfirmSignsModal';
import RejectApplicationModal from '../common/Modals/RejectApplicationModal';
import {
  getContracts,
  updateApplication,
} from '../../services/ApplicationService';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../routes/routePaths';
import { Toast, useToast } from '../../components/toast/toast';
import useRole from '../../hooks/useRole';
import { hasData, isSuccessfulResponse } from '../../utils/Utils';
import ContractStatus from './InstrumentationComponents/ContractStatus';
import InstrumentationActionButton from '../../components/instrumentation-action-button/InstrumentationActionButton';
import ReadyToInstrumentModal from '../../components/modal/ReadyToInstrumentModal';
import CommitteeButton from '../../components/committeeButton/CommitteeButton';

interface ContractProps {
  onPreviousClick: () => void;
  clientData: clientData;
  applicationId: string | undefined;
  cuit: string | undefined;
  onNextClick: Function;
}

const Contracts: React.FC<ContractProps> = ({
  onPreviousClick,
  clientData,
  applicationId,
  cuit,
  onNextClick,
}) => {
  const navigate = useNavigate();
  const { isInstrumentator, isCommercial, isComite } = useRole();
  const [isSignatureModalOpen, setIsSignatureModalOpen] =
    useState<boolean>(false);
  const [isManualSignatureModalOpen, setIsManualSignatureModalOpen] =
    useState<boolean>(false);
  const [isConfirmSignsModalOpen, setIsConfirmSignsModalOpen] =
    useState<boolean>(false);
  const [isRejectModalOpen, setRejectModalOpen] = useState<boolean>(false);
  const [currentFile, setCurrentFile] = useState<any>();
  const [currentBlobFile, setCurrentBlobFile] = useState<any>();
  const [isAddDisabled, setIsAddDisabled] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [isViewLoading, setIsViewLoading] = useState<boolean[]>([]);
  const [isSendSignature, setIsSendSignature] = useState<boolean[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [isReadyToInstrumentModalOpen, setIsReadyToInstrumentModalOpen] =
    useState<boolean>(false);
  const [isChangingStatus, setIsChangingStatus] = useState<boolean>(false);

  const contractsFormik = useFormik({
    initialValues: [
      {
        template_name: 'Garantia',
        asset_file_path: '',
        id: '',
        book_location: '',
        external_service_id: '',
        signature_type_code: '',
        status_code: '',
        isLocal: true,
      },
    ],
    onSubmit: () => {},
  });

  useEffect(() => {
    // Solo deshabilitar si no hay templates disponibles o no hay signers
    setIsAddDisabled(templates.length === 0 || clientData.signers.length === 0);
  }, [contractsFormik.values, templates.length, clientData.signers.length]);

  // Efecto para cargar contratos del backend solo al montar el componente
  useEffect(() => {
    getContracts(applicationId).then((response) => {
      if (
        hasData(response) &&
        isSuccessfulResponse(response) &&
        response.data.length > 0
      ) {
        // Filtrar contratos basándose en los signers disponibles
        const availableSignerTypes = clientData.signers.flatMap((signer) =>
          Array.isArray(signer.signer_type_code)
            ? signer.signer_type_code
            : [signer.signer_type_code]
        );
        const filteredContracts = response.data.filter((contract: any) => {
          const contractType = CONTRACTS_TYPES.find(
            (type) => type.label === contract.template_name
          );
          return contractType && availableSignerTypes.includes(contractType.id);
        });

        contractsFormik.setValues(filteredContracts);
      }
    });

    getTemplates().then((response) => {
      if (
        hasData(response) &&
        isSuccessfulResponse(response) &&
        response.data.length > 0
      ) {
        const mappedTemplates = response.data.map(
          ({ id, name }: { id: string; name: string }) => ({
            id,
            label: name,
          })
        );
        setTemplates(mappedTemplates);
      }
    });
  }, [applicationId]);

  // Efecto separado para actualizar la disponibilidad de tipos de contrato cuando cambian los signers
  useEffect(() => {
    // Actualizar estado del botón cuando cambian los signers
    setIsAddDisabled(templates.length === 0 || clientData.signers.length === 0);
  }, [clientData.signers, templates.length]);

  const isOnlySignedContracts = (): boolean => {
    const contracts = contractsFormik.values;
    const allSignedOrRejected = contracts.every(
      (contract) =>
        contract.status_code === 'SIG' || 
        contract.status_code === 'REJ'
    );
    const hasAtLeastOneSigned = contracts.some(
      (contract) => contract.status_code === 'SIG'
    );
    const hasNoPending = !contracts.some(
      (contract) => contract.status_code === 'PEN'
    );
    return allSignedOrRejected && hasAtLeastOneSigned && hasNoPending;
  };

  const addContract = () => {
    // Permitir agregar cualquier tipo disponible, incluso duplicados
    if (templates.length > 0) {
      const newContract = {
        template_name: templates[0].label, // Siempre el primer template disponible
        asset_file_path: '',
        external_service_id: '',
        book_location: '',
        id: '',
        signature_type_code: '',
        status_code: '',
        isLocal: true,
      };
      contractsFormik.setValues([...contractsFormik.values, newContract]);
    }
  };

  // Función auxiliar para verificar si un firmante tiene un tipo de firma específico
  const signerHasType = (signer: any, typeId: string): boolean => {
    const signerTypes = Array.isArray(signer.signer_type_code)
      ? signer.signer_type_code
      : [signer.signer_type_code];
    return signerTypes.includes(typeId);
  };

  const getAmount = (template: string) => {
    // Normalizar el template name (trim y case-insensitive)
    const normalizedTemplate = template?.trim();
    
    // Mapear cada tipo de contrato a su valor correspondiente
    // Fianza y Garantia usan note_amount (Valor fianza)
    // Pagare usa guarantee_amount (Valor pagaré)
    let contractAmount: number | null | undefined = undefined;
    
    if (normalizedTemplate) {
      const templateLower = normalizedTemplate.toLowerCase();
      
      if (templateLower === 'fianza' || templateLower === 'garantia') {
        contractAmount = clientData.note_amount;
      } else if (templateLower === 'pagare' || templateLower === 'pagaré') {
        contractAmount = clientData.guarantee_amount;
      }
    }
    
    // Si el valor específico del contrato existe y no es null/undefined, usarlo
    // Nota: 0 es un valor válido, solo excluimos null y undefined
    if (contractAmount !== null && contractAmount !== undefined) {
      return contractAmount;
    }
    
    // Para otros tipos de contrato o cuando no hay valor específico,
    // usar el monto aprobado en ARS como fallback
    const approvedAmountArs = clientData.approved_amount?.find(
      (item: any) => item.currency_code === CURRENCY_CODES.ars
    );
    
    return approvedAmountArs?.value;
  };

  const buildContractInfo = (template: string) => {
    const selectedContractType = CONTRACTS_TYPES.find(
      (type) => type.label === template
    );

    const mainSigner = clientData.signers.find((signer) =>
      signerHasType(signer, selectedContractType?.id || '')
    );

    const corporateMember = clientData.corporate_structure?.find(
      (member) => member.ci === mainSigner?.cuit
    );

    const signerRole = corporateMember?.position || 'Firmante Autorizado';

    const transformedSigners = clientData.signers.map((signer) => ({
      ...signer,
      ci: signer.cuit,
      position:
        signer.role || corporateMember?.position || 'Firmante Autorizado',
      address: signer.address, // Incluir el domicilio individual de cada firmante
      locality: signer.locality, // Incluir la localidad individual de cada firmante
    }));
    const filteredSigners = transformedSigners.filter((signer) =>
      signerHasType(signer, selectedContractType?.id || '')
    );

    const contractInfo = {
      client: {
        cuit: clientData.cuit,
        address: clientData.basic_data.legal_company_address || clientData.basic_data.company_address,
        city: clientData.basic_data.legal_city || clientData.basic_data.city,
        state: clientData.basic_data.legal_state || clientData.basic_data.state,
        authorizedSinger: mainSigner?.name,
        ciAuthorizedSinger: mainSigner?.cuit,
        company_name: clientData.basic_data.company_name,
        signerRole: mainSigner?.role || signerRole,
      },
      amount: getAmount(template),
      currency: CURRENCY_CODES.ars,
      signers: filteredSigners,
    };

    return contractInfo;
  };

  const handleViewPdf = (index: number) => {
    setIsViewLoading((prev) => {
      const newIsLoadingState = [...prev];
      newIsLoadingState[index] = true;
      return newIsLoadingState;
    });

    const contractInfo = buildContractInfo(
      contractsFormik.values[index].template_name
    );

    sendContractTemplate(
      contractsFormik.values[index].template_name,
      contractInfo
    ).then((response) => {
      if (hasData(response) && isSuccessfulResponse(response)) {
        const fileBlob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(fileBlob);
        window.open(url, '_blank');
        setIsViewLoading((prev) => {
          const newIsLoadingState = [...prev];
          newIsLoadingState[index] = false;
          return newIsLoadingState;
        });
      }
    });
  };

  const removeContract = (index: number) => {
    contractsFormik.setValues(
      contractsFormik.values.filter((_, i) => i !== index)
    );
  };

  const handleSignatureModal = (index: number) => {
    setSelectedIndex(index);
    setIsSendSignature((prev) => {
      const newIsLoadingState = [...prev];
      newIsLoadingState[index] = true;
      return newIsLoadingState;
    });

    const contractInfo = buildContractInfo(
      contractsFormik.values[index].template_name
    );

    sendContractTemplate(
      contractsFormik.values[index].template_name,
      contractInfo
    ).then((response) => {
      if (hasData(response) && isSuccessfulResponse(response)) {
        const fileBlob = new Blob([response.data], { type: 'application/pdf' });
        setCurrentBlobFile(fileBlob);
        fileBlob.arrayBuffer().then((buffer) => {
          const uint8Array = new Uint8Array(buffer);
          setCurrentFile(uint8Array);
          setIsSignatureModalOpen(true);
          setIsSendSignature((prev) => {
            const newIsLoadingState = [...prev];
            newIsLoadingState[index] = false;
            return newIsLoadingState;
          });
        });
      }
    });
  };

  const handleManualSignatureModal = (index: number) => {
    setSelectedIndex(index);
    setIsManualSignatureModalOpen(true);
  };

  const sendBySignature = () => {
    setIsSendSignature((prev) => {
      const newIsLoadingState = [...prev];
      newIsLoadingState[selectedIndex] = true;
      return newIsLoadingState;
    });
    setIsSignatureModalOpen(false);

    const file = new File([currentBlobFile], 'documento.pdf', {
      type: 'application/pdf',
    });
    const selectedContractLabel =
      contractsFormik.values[selectedIndex].template_name;
    const selectedContractType = CONTRACTS_TYPES.find(
      (type) => type.label === selectedContractLabel
    );
    const filteredSigners = clientData.signers.filter((signer) =>
      signerHasType(signer, selectedContractType?.id || '')
    );
    signContract(
      applicationId,
      selectedContractType?.label,
      filteredSigners,
      file
    ).then((response) => {
      if (hasData(response) && isSuccessfulResponse(response)) {
        const updatedContracts = [...contractsFormik.values];
        updatedContracts[selectedIndex] = {
          ...updatedContracts[selectedIndex],
          template_name: response.data.template_name || '',
          asset_file_path: response.data.asset_file_path || '',
          id: response.data.id?.toString() || '',
          book_location: response.data.book_location || '',
          external_service_id: response.data.external_service_id || '',
          signature_type_code: response.data.signature_type_code || '',
          status_code: response.data.status_code || '',
        };

        contractsFormik.setFormikState((prevState) => ({
          ...prevState,
          values: updatedContracts,
        }));
      } else {
        useToast.error({
          title: 'Hubo un error al firmar el contrato en signatura',
          id: 'contract-toast',
          description: '',
          closeButton: false,
          autoClose: 5000,
        });
      }
      setIsSendSignature((prev) => {
        const newIsLoadingState = [...prev];
        newIsLoadingState[selectedIndex] = false;
        return newIsLoadingState;
      });
    });
  };

  const sendByManualSignature = (contractData: any) => {
    if (contractData && contractData.file) {
      setIsManualSignatureModalOpen(false);
      const manualContractInfo: manualContractData = {
        template_name: contractsFormik.values[selectedIndex].template_name,
        asset_file_path: '',
        book_location: contractData.book_location,
      };
      signManualContract(applicationId, manualContractInfo, contractData.file).then((response) => {
        if (hasData(response) && isSuccessfulResponse(response)) {
          const updatedContracts = [...contractsFormik.values];
          updatedContracts[selectedIndex] = {
            ...updatedContracts[selectedIndex],
            template_name: response.data.template_name || '',
            asset_file_path: response.data.asset_file_path || '',
            id: response.data.id?.toString() || '',
            book_location: response.data.book_location || '',
            external_service_id: response.data.external_service_id || '',
            signature_type_code: response.data.signature_type_code || '',
            status_code: response.data.status_code || '',
            isLocal: true,
          };

          contractsFormik.setFormikState((prevState) => ({
            ...prevState,
            values: updatedContracts,
          }));
        } else {
          useToast.error({
            title: 'Hubo un error al firmar el contrato manualmente',
            id: 'contract-toast',
            description: '',
            closeButton: false,
            autoClose: 5000,
          });
        }
      });
    }
  };

  const handleApplication = (
    status: string,
    rejectText: string,
    options?: {
      onSuccess?: () => void;
      onError?: () => void;
      showLoading?: boolean;
    }
  ) => {
    if (options?.showLoading) setIsChangingStatus(true);

    updateApplication(
      cuit || '',
      { status_code: status, comment: rejectText },
      applicationId || ''
    )
      .then((response) => {
        const mapStatus = new Map();
        mapStatus.set(
          APPLICATION_STATUS.approved.label,
          'Aceptaste la documentación. El productor será notificado.'
        );
        mapStatus.set(
          APPLICATION_STATUS.rejected.label,
          'Declinaste la solicitud. El productor será notificado.'
        );
        mapStatus.set(
          APPLICATION_STATUS.pending.label,
          'Solicitaste cambios en la solicitud. El productor será notificado.'
        );
        mapStatus.set(
          APPLICATION_STATUS.readyToInstrument.label,
          'Estado cambiado a "Para instrumentar"'
        );
        if (response.status === 200) {
          useToast.success({
            title: mapStatus.get(status),
            id: 'contract-toast',
            description: '',
            closeButton: false,
            autoClose: 5000,
          });
          options?.onSuccess?.();
          setTimeout(() => {
            navigate(routes.dashboard);
          }, 1000);
        }
      })
      .catch((error) => {
        useToast.error({
          title: 'Hubo un error al cambiar el estado',
          id: 'contract-toast',
          description: error.response?.data?.message || 'Error desconocido',
          closeButton: false,
          autoClose: 5000,
        });
        options?.onError?.();
      })
      .finally(() => {
        if (options?.showLoading) setIsChangingStatus(false);
      });
  };

  const handleDeleteContract = (contractId: string) => {
    deleteContractFile(applicationId, contractId).then((response) => {
      if (hasData(response) && isSuccessfulResponse(response)) {
        const updatedContracts = [...contractsFormik.values];
        updatedContracts[selectedIndex] = {
          ...updatedContracts[selectedIndex],
          template_name: response.data.template_name || '',
          asset_file_path: response.data.asset_file_path || '',
          id: response.data.id?.toString() || '',
          book_location: response.data.book_location || '',
          external_service_id: response.data.external_service_id || '',
          signature_type_code: response.data.signature_type_code || '',
          status_code: response.data.status_code || '',
        };

        contractsFormik.setFormikState((prevState) => ({
          ...prevState,
          values: updatedContracts,
        }));
      } else {
        useToast.error({
          title: 'Hubo un error al eliminar el contrato',
          id: 'contract-toast',
          description: '',
          closeButton: false,
          autoClose: 5000,
        });
      }
    });
  };

  return (
    <Card spacing={{ left: '6' }}>
      <Toast id='contract-toast' />
      <Toast id='committee-toast' />
      <SignatureModal
        isOpen={isSignatureModalOpen}
        file={currentFile}
        onClose={() => setIsSignatureModalOpen(false)}
        onAccept={sendBySignature}
      />
      <ManualSignatureModal
        isOpen={isManualSignatureModalOpen}
        onClose={() => setIsManualSignatureModalOpen(false)}
        onAccept={(e: any) => sendByManualSignature(e)}
        cuit={cuit}
      />
      <ConfirmSignsModal
        isOpen={isConfirmSignsModalOpen}
        onClose={() => setIsConfirmSignsModalOpen(false)}
        onAccept={onNextClick}
      />
      <RejectApplicationModal
        isOpen={isRejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onSecondaryAction={() => setRejectModalOpen(false)}
        onPrimaryAction={(rejectText: string) => {
          setRejectModalOpen(false);
          handleApplication(APPLICATION_STATUS.rejected.label, rejectText);
        }}
      />
      <ReadyToInstrumentModal
        isOpen={isReadyToInstrumentModalOpen}
        onClose={() => setIsReadyToInstrumentModalOpen(false)}
        onSecondaryAction={() => setIsReadyToInstrumentModalOpen(false)}
        onPrimaryAction={() => {
          setIsReadyToInstrumentModalOpen(false);
          handleApplication(
            APPLICATION_STATUS.readyToInstrument.label,
            'Cambio de estado a Para instrumentar',
            { showLoading: true }
          );
        }}
        loadingPrimaryBtn={isChangingStatus}
      />
      <Card display='grid' gap='8px' margin={{ bottom: '4' }}>
        <P
          type='1'
          variant='neutral-1'
          withMargin={false}
          weight='bold'
          modifier='strong'
        >
          Contratos
        </P>
        <P type='1' withMargin={false} variant='neutral' modifier='regular'>
          Generá los contratos y hacelos firmar por el cliente, ya sea a través
          de Signatura o subiendo el archivo firmado.
        </P>
      </Card>
      <form onSubmit={contractsFormik.handleSubmit}>
        {contractsFormik.values.map((contract: contractData, index: number) => (
          <Card key={index} display='flex' margin={{ bottom: '3' }}>
            <Card width='40%'>
              {index === 0 && (
                <P type='2' variant='neutral' modifier='strong'>
                  Template de contrato
                </P>
              )}
              <Card display='flex' gap='16px'>
                <Card width='70%'>
                  <DropDown
                    size='md'
                    id='contract-type-dropdown'
                    items={templates}
                    title={
                      templates.find(
                        (item) => item.label === contract?.template_name
                      )?.label
                    }
                    onChange={(e: string) =>
                      contractsFormik.setFieldValue(
                        `[${index}].template_name`,
                        templates.find((item) => item.label === e)?.label
                      )
                    }
                  />
                </Card>
                <Card
                  width='12%'
                  display='flex'
                  justifyContent='center'
                  alignItems='center'
                >
                  {isViewLoading[index] ? (
                    <Spinner size='sm' />
                  ) : (
                    <Button
                      size='md'
                      variant='secondary'
                      onClick={() => {
                        handleViewPdf(index);
                      }}
                    >
                      <Card
                        variant='none'
                        margin={{
                          top: '2',
                          left: '0',
                          bottom: '2',
                          right: '0',
                        }}
                        height='28px'
                      >
                        <Icon
                          variant='neutral'
                          name='view_outline'
                          cursor='pointer'
                        />
                      </Card>
                    </Button>
                  )}
                </Card>
                <Icon variant='neutral' name='chevron_right' />
              </Card>
            </Card>
            <Card display='flex' gap='8px' width='60%' alignItems='end'>
              <Card width='100%'>
                {index === 0 && (
                  <P type='2' variant='neutral' modifier='strong'>
                    Contrato firmado
                  </P>
                )}
                {contract.status_code && (
                  <Card width='100%'>
                    <ContractStatus
                      cuit={cuit}
                      applicationId={applicationId}
                      contract={contract}
                      onDelete={(e: string) => handleDeleteContract(e)}
                    />
                  </Card>
                )}
                {!contract.signature_type_code && (
                  <Card display='flex' gap='12px'>
                    <Button
                      variant='secondary'
                      size='lg'
                      icon='quill_pen_outline'
                      iconAlign='left'
                      onClick={() => handleSignatureModal(index)}
                    >
                      {isSendSignature[index] ? (
                        <Spinner size='sm' />
                      ) : (
                        'Enviar por Signatura'
                      )}
                    </Button>
                    <Button
                      variant='secondary'
                      size='lg'
                      icon='upload_outline'
                      iconAlign='left'
                      onClick={() => handleManualSignatureModal(index)}
                    >
                      Subir contrato firmado
                    </Button>
                    {index !== 0 && (
                      <Button
                        size='md'
                        variant='secondary'
                        onClick={() => removeContract(index)}
                      >
                        <Card
                          variant='none'
                          margin={{
                            top: '2',
                            left: '0',
                            bottom: '2',
                            right: '0',
                          }}
                        >
                          <Icon
                            variant='neutral'
                            name='delete_outline'
                            cursor='pointer'
                            height={28}
                          />
                        </Card>
                      </Button>
                    )}
                  </Card>
                )}
              </Card>
            </Card>
          </Card>
        ))}
      </form>
      <Card margin={{ top: '2' }}>
        <Button
          id='signers-add-button'
          variant='tertiary'
          size='md'
          onClick={addContract}
          disabled={isAddDisabled}
        >
          + Agregar contrato
        </Button>
      </Card>
      <Card display='flex' justifyContent='space-between' margin={{ top: '5' }}>
        <Card width='200px'>
          <Button
            id='client-button-back'
            size='lg'
            variant='secondary'
            onClick={() => onPreviousClick()}
            fullWidth
          >
            Volver
          </Button>
        </Card>
        <Card display='flex' justifyContent='flex-end' gap='16px' width='900px'>
          {isOnlySignedContracts() && (isCommercial || isComite || isInstrumentator) ? (
            <InstrumentationActionButton
              onDecline={() => setRejectModalOpen(true)}
              onReadyToInstrument={() => setIsReadyToInstrumentModalOpen(true)}
            />
          ) : (
            <>
              <CommitteeButton
                cuit={cuit}
                applicationId={applicationId}
                variant='custom'
                size='lg'
                width='100%'
                height='50px'
              />
              <Button
                id='client-button-decline'
                size='md'
                variant='secondary'
                onClick={() => {
                  setRejectModalOpen(true);
                }}
                fullWidth
              >
                {false ? <Spinner size='sm' variant='primary' /> : 'Declinar'}
              </Button>
            </>
          )}
          {isInstrumentator ? (
            <Button
              id='client-button-save'
              size='lg'
              variant='primary'
              onClick={() => setIsConfirmSignsModalOpen(true)}
              fullWidth
              disabled={!isOnlySignedContracts()}
            >
              {false ? <Spinner size='sm' variant='primary' /> : 'Instrumentar'}
            </Button>
          ) : (
            <Button
              id='client-button-save'
              size='lg'
              variant='primary'
              onClick={() => navigate(routes.dashboard)}
              fullWidth
            >
              Volver al dashboard
            </Button>
          )}
        </Card>
      </Card>
    </Card>
  );
};

export default Contracts;
